import { __awaiter } from "tslib";
import { action, makeObservable, observable } from "mobx";
import { NodeLiveOutputTypes } from "../../plugins/dataflow/model/utilities/node";
import { parseKeyValueData } from "./serial-protocol";
import { deviceProtocol } from "../../plugins/dataflow/model/utilities/device-capabilities";
import { CcDeviceSession } from "./cc-device-session";
function log(message) {
    // eslint-disable-next-line no-console
    console.log(`[SerialDevice] ${message}`);
}
export class SerialDevice {
    constructor() {
        Object.defineProperty(this, "localBuffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connectChangeStamp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastConnectMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceFamily", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // The active write transport (Web Serial or WebUSB). writeLine routes here.
        Object.defineProperty(this, "activeTransport", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // The CC Device Protocol session of the active device, when it speaks that protocol.
        Object.defineProperty(this, "ccDeviceSession", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Cleared by `setActiveDevice` and `clearLastLoss`.
        Object.defineProperty(this, "lastLoss", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // True once the active transport has delivered any data. Reset by `setActiveDevice` and
        // `clearActiveDevice`.
        Object.defineProperty(this, "dataReceived", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // Functions that each return the channels of one tile. Inbound data is delivered to the
        // channels of every registered source.
        Object.defineProperty(this, "channelSources", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        /**
         * True when we think the connected Web Serial board will raise the browser `connect` event.
         * It drives connect-button physical-connection UI. Only ever set
         * by the Web Serial path — stays false for a WebUSB (Spiker:bit) connection.
         */
        Object.defineProperty(this, "raisesWebSerialConnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.localBuffer = "";
        makeObservable(this, {
            activeTransport: observable.ref,
            ccDeviceSession: observable.ref,
            lastLoss: observable.ref,
            deviceFamily: observable,
            dataReceived: observable,
            setActiveDevice: action,
            clearActiveDevice: action,
            recordUnplug: action,
            recordLoss: action,
            clearLastLoss: action,
            markDataReceived: action
        });
    }
    // Registers a tile's channels to receive inbound data. Returns a disposer.
    registerChannelSource(getChannels) {
        this.channelSources.add(getChannels);
        return () => { this.channelSources.delete(getChannels); };
    }
    get allChannels() {
        return Array.from(this.channelSources).flatMap(getChannels => getChannels());
    }
    // TODO, revise this so it is more clear how its used
    updateConnectionInfo(timeStamp, status) {
        this.connectChangeStamp = timeStamp;
        this.lastConnectMessage = status;
        localStorage.setItem("last-connect-message", status);
    }
    setRaisesWebSerialConnect(raises) {
        this.raisesWebSerialConnect = raises;
    }
    isConnected() {
        return this.activeTransport != null;
    }
    // Called by a transport (Web Serial or WebUSB) to become the active write path, receive
    // inbound data centrally, and mark the store connected.
    setActiveDevice(deviceFamily, transport) {
        var _a;
        // Start each session with an empty parse buffer so a partial line left by a prior
        // connection can't combine with new data into a spurious message.
        this.localBuffer = "";
        (_a = this.ccDeviceSession) === null || _a === void 0 ? void 0 : _a.stop();
        this.ccDeviceSession = undefined;
        this.lastLoss = undefined;
        this.dataReceived = false;
        this.activeTransport = transport;
        this.deviceFamily = deviceFamily;
        transport.onData = (chunk) => {
            if (this.activeTransport !== transport)
                return;
            this.markDataReceived();
            this.receive(chunk);
        };
        transport.onDisconnect = (loss) => {
            if (loss === "unplugged")
                this.recordUnplug(transport);
            else if (this.activeTransport === transport)
                this.clearActiveDevice(loss);
        };
        if (deviceProtocol(deviceFamily) === "ccDevice") {
            // Assigned before start() so a reply that arrives during the first write is routed.
            const session = new CcDeviceSession(line => transport.write(line));
            this.ccDeviceSession = session;
            session.start();
        }
        this.updateConnectionInfo(Date.now(), "connect");
    }
    // Ends the CC Device Protocol session: writes `close` when a session is open or an open is
    // still pending. Resolves at once when there is no session.
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield ((_a = this.ccDeviceSession) === null || _a === void 0 ? void 0 : _a.close());
        });
    }
    // With a `loss`, records that the active device was lost without the user asking.
    clearActiveDevice(loss) {
        var _a;
        if (loss && this.activeTransport) {
            this.lastLoss = { reason: loss, transport: this.activeTransport, at: Date.now() };
        }
        (_a = this.ccDeviceSession) === null || _a === void 0 ? void 0 : _a.stop();
        this.ccDeviceSession = undefined;
        this.activeTransport = undefined;
        this.deviceFamily = undefined;
        this.dataReceived = false;
        this.localBuffer = "";
        // Reset so a prior Web Serial session's value can't stick to a later WebUSB device, which
        // never raises the browser connect/disconnect events this flag gates the connect-button UI on.
        this.raisesWebSerialConnect = false;
        this.updateConnectionInfo(Date.now(), "disconnect");
    }
    // The browser reported `transport`'s device removed. Clears it if it's still active. If another
    // path already cleared it as lost (a failed write, or the read loop giving up, can come first),
    // the loss becomes an unplug, keeping its time.
    recordUnplug(transport) {
        var _a;
        if (this.activeTransport === transport) {
            this.clearActiveDevice("unplugged");
        }
        else if (((_a = this.lastLoss) === null || _a === void 0 ? void 0 : _a.transport) === transport && this.lastLoss.reason === "lost") {
            this.lastLoss = Object.assign(Object.assign({}, this.lastLoss), { reason: "unplugged" });
        }
    }
    // Records the loss of a device that was open but never became active, such as a Spiker:bit
    // unplugged during its firmware update.
    recordLoss(transport, reason) {
        this.lastLoss = { reason, transport, at: Date.now() };
    }
    clearLastLoss() {
        this.lastLoss = undefined;
    }
    markDataReceived() {
        this.dataReceived = true;
    }
    // Central inbound router: dispatch a serial chunk to the parser for the connected
    // device's protocol, using the channels of every registered source.
    receive(chunk) {
        var _a;
        const protocol = deviceProtocol(this.deviceFamily);
        if (protocol === "keyValue")
            this.handleKeyValueStreamObj(chunk, this.allChannels);
        if (protocol === "radioHub")
            this.handleRadioHubStreamObj(chunk, this.allChannels);
        if (protocol === "ccDevice")
            (_a = this.ccDeviceSession) === null || _a === void 0 ? void 0 : _a.receive(chunk, this.allChannels);
    }
    handleRadioHubStreamObj(value, channels) {
        this.localBuffer += value;
        // [sc]   signal or control
        // [abcd] which micro:bit
        // [rth]  relay, temp, humidity
        const pattern = /([sc]{1})([abcd]{1})([rth]{1})([0-9.]+)\s{0,}[\r][\n]/g;
        let match;
        do {
            match = pattern.exec(this.localBuffer);
            if (!match)
                break;
            const [fullMatch, signalType, microbitId, element, reading] = match;
            this.localBuffer = this.localBuffer.substring(match.index + fullMatch.length);
            const targetChannelId = `${element}-${microbitId}`;
            const targetChannels = channels.filter((c) => c.channelId === targetChannelId);
            if (signalType === "s") {
                targetChannels.forEach(targetChannel => {
                    if (["h", "t"].includes(element)) {
                        // handle message from a humidity or temperature sensor
                        if (isFinite(Number(reading))) {
                            targetChannel.value = Number(reading);
                        }
                        targetChannel.lastMessageReceivedAt = Date.now();
                    }
                    if (["r"].includes(element)) {
                        // handle message about relays state
                        targetChannel.relaysState = reading.split('').map(s => Number(s));
                        targetChannel.lastMessageReceivedAt = Date.now();
                    }
                });
            }
        } while (match);
    }
    handleKeyValueStreamObj(value, channels) {
        this.localBuffer = parseKeyValueData(this.localBuffer + value, channels);
    }
    writeLine(line) {
        if (this.activeTransport) {
            this.activeTransport.write(line);
        }
        else {
            log("Port closed, skipping write");
        }
    }
    writeToOutForMicroBitRelayHub(data, hubId, relayType) {
        const ri = NodeLiveOutputTypes.filter((ot) => ot.name === relayType)[0].relayIndex;
        const controlMessage = `c${hubId}${ri}${data}`;
        this.writeLine(controlMessage);
    }
    writeToOutForBBGripper(n, liveOutputType) {
        const outputConfig = NodeLiveOutputTypes.find(o => o.name === liveOutputType);
        if (this.isConnected() && (outputConfig === null || outputConfig === void 0 ? void 0 : outputConfig.angleBase) !== undefined) {
            const percent = n / 100;
            const openTo = Math.round(outputConfig.angleBase - (percent * outputConfig.sweep));
            this.writeLine(openTo.toString());
        }
    }
    writeToOutForServo(n, liveOutputType) {
        var _a;
        const outputConfig = NodeLiveOutputTypes.find(o => o.name === liveOutputType);
        if (this.isConnected() && (outputConfig === null || outputConfig === void 0 ? void 0 : outputConfig.angleOffset) !== undefined) {
            const scaledAngle = (outputConfig.angleScale * n) + outputConfig.angleOffset;
            const roundedScaled = Math.round(scaledAngle);
            if (deviceProtocol(this.deviceFamily) === "ccDevice") {
                (_a = this.ccDeviceSession) === null || _a === void 0 ? void 0 : _a.sendOutput("servo", roundedScaled);
            }
            else {
                this.writeLine(roundedScaled.toString());
            }
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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